import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useDocumentTitle } from "@refinedev/react-router";
import { CalendarDaysIcon, InfoIcon, PencilIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import {
  DetailField,
  DetailGrid,
  DetailSection,
  EmptyHint,
  ShowHero,
  SystemSection,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeleteGroupDialog } from "@/pages/event-groups/components/delete-group-dialog";
import {
  BackLink,
  GROUPS_VIEW,
  GroupGone,
  GroupLoadFailed,
  GroupLoading,
} from "@/pages/event-groups/components/group-page-states";
import { useEventGroupQuery } from "@/pages/event-groups/hooks/use-event-group-query";
import { formatStart } from "@/pages/event-groups/lib/format-start";
import {
  type EventGroup,
  type GroupEvent,
  MAX_EVENTS_PER_GROUP,
} from "@/types/event-group";

function EventRow({ event }: { event: GroupEvent }) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <Link to={`/events/edit/${event.id}`} className="hover:underline">
          {event.name}
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        {formatStart(event.startDate, event.timezone)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {event.venue?.name ?? "—"}
      </TableCell>
      <TableCell>
        {event.status ? <Badge variant="outline">{event.status}</Badge> : "—"}
      </TableCell>
    </TableRow>
  );
}

/** The member events as the backend sends them, oldest start first. No client-side re-sort. */
function GroupEvents({ events }: { events: GroupEvent[] }) {
  return (
    <DetailSection
      title="Events"
      icon={<CalendarDaysIcon className="size-4" />}
      action={
        <Badge variant="secondary">
          {events.length} of {MAX_EVENTS_PER_GROUP}
        </Badge>
      }
    >
      {events.length === 0 ? (
        <EmptyHint>No events in this group yet.</EmptyHint>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Starts</TableHead>
              <TableHead>Venue</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </TableBody>
        </Table>
      )}
    </DetailSection>
  );
}

function GroupDetails({ group }: { group: EventGroup }) {
  return (
    <DetailSection title="Details" icon={<InfoIcon className="size-4" />}>
      <DetailGrid>
        <DetailField label="Slug" value={group.slug} mono />
        <DetailField label="Saves" value={group.interestedIn} />
        <DetailField label="ID" mono>
          <span className="inline-flex items-center gap-1">
            {group.id}
            <CopyButton text={group.id} message="Copied group ID" />
          </span>
        </DetailField>
      </DetailGrid>
    </DetailSection>
  );
}

/**
 * A Mood event group: its photo, text and audit trail, and the events it holds. A group with an
 * `externalKey` was made by Tina's festival job, so it says so and links to the festival record.
 */
export const EventGroupShow = () => {
  const { id } = useParams();
  const setTitle = useDocumentTitle();
  const query = useEventGroupQuery("show", id);
  const navigate = useNavigate();

  const name = query.data?.group.name;
  useEffect(() => {
    if (name) setTitle(`Event group | ${name}`);
  }, [name]);

  if (query.isPending) return <GroupLoading />;

  if (query.isError) {
    return (
      <GroupLoadFailed error={query.error} onRetry={() => query.refetch()} />
    );
  }

  if (!query.data) return <GroupGone />;

  const { group, events } = query.data;

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <BackLink />
          <div className="flex items-center gap-2">
            <Button asChild variant="outline">
              <Link to={`/event-groups/edit/${group.id}`}>
                <PencilIcon className="size-4" />
                Edit
              </Link>
            </Button>
            <DeleteGroupDialog
              group={group}
              eventCount={events.length}
              onDone={() => navigate(GROUPS_VIEW)}
            />
          </div>
        </div>

        <ShowHero
          poster={group.media?.photos?.[0]}
          eyebrow="Event group"
          title={group.name}
          badges={
            group.externalKey ? (
              <Badge variant="secondary">Made by Tina's festival job</Badge>
            ) : undefined
          }
          description={group.about}
        />

        <GroupEvents events={events} />

        <GroupDetails group={group} />

        <SystemSection record={group} />
      </div>
    </div>
  );
};
